import { createEnrichmentIsland } from "@/utils/islands";
import {
    openModal,
    hideSpinner,
    showSpinner,
} from "@sitecorecomponents/BasicContent/Modal";
import { openLogin } from "../Login/login";
import {
    emailRules,
    passwordRules,
    requiredRule,
    setupFieldValidation,
    setupPasswordChecklist,
    setupPasswordToggle,
} from "../formValidation";
import { showFormMessage } from "../formMessages";
import { refreshLoginState } from "../Login/LoginStateElement";
import { hasValidToken } from "@sitecorecomponents/Users/Store/tokenstore";
import { resolveRedirectUrl } from "../redirect";
import {
    authenticateWithCredentials,
} from "../authApi";
import {
    createUserProfileMutation,
    CreateUserProfileMutationResult, SignupResult
} from "@sitecorecomponents/Users/Signup/signup.query";
import {fetchApiGraphQL, GraphQLError} from "@/utils/fetcher";

type SignupErrorKey =
    | NonNullable<SignupResult["reason"]>
    | "password-complexity";

function showState(form: HTMLFormElement, state: string) {
    form.querySelectorAll<HTMLElement>("[data-state]").forEach((elm) => {
        elm.hidden = elm.dataset.state !== state;
    });
}

export type OpenSignupOptions = {
    /** Leave the current page when the signup succeeds. Off by default: the modal callers
     *  (login link, apply-to-open-house) opened signup to get past a gate, not to navigate. */
    redirect?: boolean;
};

function setupSignupValidation(form: HTMLFormElement) {
    form.querySelectorAll<HTMLInputElement>("#FirstName, #LastName, #PhoneNumber")
        .forEach((input) => setupFieldValidation(input, [requiredRule]));

    const email = form.querySelector<HTMLInputElement>("#Email");
    const password = form.querySelector<HTMLInputElement>("#Password");

    if (email) {
        setupFieldValidation(email, emailRules);
    }

    if (password) {
        setupFieldValidation(password, [requiredRule]);
        setupPasswordChecklist(password);
        setupPasswordToggle(password);
    }
}

function wireSignupLinks(
    form: HTMLFormElement,
    dialog?: HTMLDialogElement | null,
) {
    form.querySelector<HTMLAnchorElement>("#open-login")?.addEventListener(
        "click",
        (e) => {
            e.preventDefault();
            dialog?.close();
            openLogin();
        },
    );
}

function initSignupForm(
    form: HTMLFormElement,
    dialog?: HTMLDialogElement | null,
    options: OpenSignupOptions = {},
) {
    form.onsubmit = (e) => handleSubmit(e, options);
    setupSignupValidation(form);
    wireSignupLinks(form, dialog);
}
async function createUser(firstName: string, lastName: string, phoneNumber: string, email: string, password: string): Promise<SignupResult> {
    let result: CreateUserProfileMutationResult;
    try {
        result = await fetchApiGraphQL<CreateUserProfileMutationResult>(
            createUserProfileMutation,
            { firstName, lastName, phoneNumber, email, password }
        );
    } catch (err) {
        const message = (err as GraphQLError)?.errors?.[0]?.message ?? '';
        const lower = message.toLowerCase();

        if (lower.includes('email') && (lower.includes('exist') || lower.includes('already'))) {
            return { success: false, reason: 'email-exists' };
        }
        return { success: false, reason: 'signup-failed' };
    }

    if (!result?.data?.createUserProfile) {
        return { success: false, reason: 'signup-failed' };
    }

    return { success: true };
}
const handleSubmit = async (e: SubmitEvent, options: OpenSignupOptions) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const firstName = (
        form.querySelector<HTMLInputElement>("#FirstName")?.value ?? ""
    ).trim();
    const lastName = (
        form.querySelector<HTMLInputElement>("#LastName")?.value ?? ""
    ).trim();
    const phoneNumber = (
        form.querySelector<HTMLInputElement>("#PhoneNumber")?.value ?? ""
    ).trim();
    const email = (
        form.querySelector<HTMLInputElement>("#Email")?.value ?? ""
    ).trim();
    const password =
        form.querySelector<HTMLInputElement>("#Password")?.value ?? "";

    const showSignupError = (key: SignupErrorKey) => showFormMessage(form, key);

    showFormMessage(form, null);

    if (passwordRules.some((rule) => !rule.test(password))) {
        showSignupError("password-complexity");
        return;
    }

    try {
        showSpinner();
        const created = await createUser(firstName, lastName, phoneNumber, email, password);
        if (!created.success) {
            showSignupError(created.reason ?? "signup-failed");
            return;
        }

        const auth = await authenticateWithCredentials(email, password);
        if (!auth.success) {
            showSignupError("signup-failed");
            return;
        }

        if (options.redirect) {
            const redirectUrl = resolveRedirectUrl(form);
            if (redirectUrl) {
                document.location.assign(redirectUrl);
                return;
            }
        }
    } finally {
        hideSpinner();
    }

    refreshLoginState();
    showState(form, "success");
};

export const openSignup = async (options: OpenSignupOptions = {}) => {
    const form = document.createElement("form");

    return new Promise<boolean>((resolve) => {
        openModal({
            name: "opret-bruger",
            insertContent: (d) => {
                const innerHtml = d.innerHTML;
                d.innerHTML = "";

                form.innerHTML = innerHtml;
                d.appendChild(form);

                initSignupForm(form, d.closest("dialog"), options);
            },
            onClose: () => {
                form.onsubmit = null;
                resolve(hasValidToken());
            },
        });
    });
};

function initDirectSignupForms() {
    createEnrichmentIsland(".signup-form", (elm) => {
        const form = elm as HTMLFormElement;
        initSignupForm(form, null, { redirect: true });
    });
}
export default function initSignupButton() {
    createEnrichmentIsland("[signup-btn]", (elm) => {
        elm.addEventListener("click", (e) => {
            e.preventDefault();
            openSignup();
        });
    });
    initDirectSignupForms();
}
